import { Eye, EyeOff, Loader2, Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CopyButton } from '@/components/copy-button';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError, api } from '@/lib/api-client';
import { formatMoney } from '@/lib/format';
import { generateTotpCode, totpSecondsRemaining } from '@/lib/totp';
import type { CatalogGame, PsnAccount, RevealCredentialsResponse } from '@/lib/types';
import { cn } from '@/lib/utils';

const TOTP_PERIOD = 30;

interface Creds {
  email: string;
  password: string | null;
  totp_secret: string | null;
}

interface Props {
  account: PsnAccount;
  /** Chamado após registrar compra com sucesso (refetch → pipeline avança). */
  onPurchased: () => void;
}

/**
 * Q-246 Modo Compra: tela de trabalho do operador enquanto compra o jogo na
 * PSN. Reveal sem dialog de motivo (reason fixo 'modo_compra'), copy targets
 * grandes, TOTP ao vivo e form de registrar a compra.
 *
 * Higiene: os segredos vivem SÓ no estado deste componente — a tab inativa é
 * desmontada pelo Radix Tabs, então sair da tab/rota descarta tudo. Nada de
 * segredo em log/console.
 */
export function PurchaseModeTab({ account, onPurchased }: Props) {
  const [searchParams] = useSearchParams();

  // ── Reveal ────────────────────────────────────────────────────────────────
  const [creds, setCreds] = useState<Creds | null>(null);
  const [revealLoading, setRevealLoading] = useState(true);
  const [revealError, setRevealError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  // Guard contra o double-invoke do StrictMode (não queimar rate limit 2x).
  const revealedFor = useRef<string | null>(null);

  useEffect(() => {
    if (revealedFor.current === account.id) return;
    revealedFor.current = account.id;
    setRevealLoading(true);
    setRevealError(null);
    api<RevealCredentialsResponse>(`/psn-accounts/${account.id}/reveal`, {
      method: 'POST',
      body: { reason: 'modo_compra', context: 'purchase_mode' },
    })
      .then((res) => {
        setCreds({
          email: res.data.email,
          password: res.data.password,
          totp_secret: res.data.totp_secret,
        });
      })
      .catch((err) => {
        if (err instanceof ApiError && err.errorCode === 'rate_limited') {
          const retry = (err.body as { retry_after_sec?: number })?.retry_after_sec ?? 0;
          setRevealError(`Limite de reveals atingido. Tente de novo em ~${Math.ceil(retry / 60)} min.`);
        } else {
          setRevealError('Falha ao revelar credenciais.');
        }
      })
      .finally(() => setRevealLoading(false));
  }, [account.id]);

  // ── TOTP ao vivo ──────────────────────────────────────────────────────────
  const [totpCode, setTotpCode] = useState<string | null>(null);
  const [totpSeconds, setTotpSeconds] = useState(TOTP_PERIOD);

  useEffect(() => {
    const secret = creds?.totp_secret;
    if (!secret) return;
    let active = true;
    const tick = async () => {
      setTotpSeconds(totpSecondsRemaining());
      try {
        const code = await generateTotpCode(secret);
        if (active) setTotpCode(code);
      } catch {
        if (active) setTotpCode(null);
      }
    };
    void tick();
    const iv = window.setInterval(() => void tick(), 1000);
    return () => {
      active = false;
      clearInterval(iv);
      setTotpCode(null);
    };
  }, [creds?.totp_secret]);

  // ── Picker de jogo (GET /catalog/psn-games?q=) ────────────────────────────
  // ?titulo= (vindo da Fila de Compras) semeia a busca e dispara o fetch.
  const [searchQ, setSearchQ] = useState(() => searchParams.get('titulo')?.trim() ?? '');
  const [catalog, setCatalog] = useState<CatalogGame[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [selectedRef, setSelectedRef] = useState<string>('');
  const prefilled = useRef(false);

  useEffect(() => {
    const q = searchQ.trim();
    // O endpoint exige q>=2 chars — não martelar a API com 400 a cada keystroke.
    if (q.length < 2) {
      setCatalog([]);
      setCatalogLoading(false);
      return;
    }
    let active = true;
    const t = window.setTimeout(() => {
      setCatalogLoading(true);
      api<{ data?: CatalogGame[] } | CatalogGame[]>('/catalog/psn-games', {
        query: { q },
      })
        .then((res) => {
          if (!active) return;
          setCatalog(Array.isArray(res) ? res : (res.data ?? []));
        })
        .catch(() => {
          if (active) setCatalog([]);
        })
        .finally(() => {
          if (active) setCatalogLoading(false);
        });
    }, 300);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [searchQ]);

  // product_ref pode ser null (jogo sem linha PS5 Primária) — o value do
  // SelectItem cai pro game_title nesses casos (Radix não aceita value vazio).
  const catalogValue = (g: CatalogGame) => g.product_ref ?? g.game_title;
  const selectedGame = catalog.find((g) => catalogValue(g) === selectedRef) ?? null;

  // ── Form registrar compra ────────────────────────────────────────────────
  const [price, setPrice] = useState('');
  // ?acessos= (vindo da Fila de Compras) pré-marca o radio 3/5.
  const [accessCount, setAccessCount] = useState<3 | 5>(() =>
    searchParams.get('acessos') === '5' ? 5 : 3,
  );
  const balances = account.balance ?? [];
  const defaultCurrency =
    [...balances].sort((a, b) => Number(b.available) - Number(a.available))[0]?.currency ?? 'USD';
  const [currency, setCurrency] = useState(defaultCurrency);
  const [submitting, setSubmitting] = useState(false);

  // Pré-seleção via ?jogo=<product_ref> vindo da Fila de Compras (a busca já
  // foi semeada com ?titulo=). Sem match no resultado, o título livre da busca
  // vale como fallback (effectiveTitle abaixo).
  const initialRef = searchParams.get('jogo');
  useEffect(() => {
    if (prefilled.current || !initialRef || catalog.length === 0) return;
    const found = catalog.find((g) => g.product_ref === initialRef);
    if (found) {
      prefilled.current = true;
      setSelectedRef(catalogValue(found));
      if (found.access_count === 3 || found.access_count === 5) setAccessCount(found.access_count);
    }
  }, [catalog, initialRef]);

  const currencyOptions = Array.from(new Set([...balances.map((b) => b.currency), 'USD', 'BRL']));
  const selBalance = balances.find((b) => b.currency === currency);
  const priceNum = Number(price.replace(',', '.'));
  const priceValid = Number.isFinite(priceNum) && priceNum > 0;
  const insufficient = priceValid && Number(selBalance?.available ?? 0) < priceNum;

  // Sem item selecionado no catálogo, o texto da busca vale como título livre
  // (cobre catálogo vazio E prefill da Fila sem match — a tela não pode ficar
  // inutilizável).
  const effectiveTitle = selectedGame?.game_title ?? searchQ.trim();

  const onConfirm = async () => {
    if (!effectiveTitle || !priceValid || submitting) return;
    setSubmitting(true);
    try {
      await api(`/psn-accounts/${account.id}/purchases`, {
        method: 'POST',
        body: {
          game_title: effectiveTitle,
          product_ref: selectedGame?.product_ref || undefined,
          shopify_product_id: selectedGame?.shopify_product_id || undefined,
          price: priceNum,
          currency,
          access_count: accessCount,
        },
      });
      toast.success('Compra registrada. Conta pronta pra importar.');
      setPrice('');
      onPurchased();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        toast.error('Conta precisa estar Disponível ou Com saldo pra registrar compra.');
      } else {
        toast.error('Falha ao registrar compra.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[640px] space-y-4">
      {/* 1. Saldo */}
      <Card>
        <CardContent className="space-y-2 py-4">
          {balances.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Sem saldo registrado nesta conta.{' '}
              <Link to="/topup" className="text-primary underline-offset-4 hover:underline">
                Resgatar gift card
              </Link>
            </p>
          ) : (
            balances.map((b) => (
              <div key={b.currency} className="flex flex-wrap items-center gap-2">
                <Badge
                  variant={insufficient && b.currency === currency ? 'warning' : 'success'}
                  className="px-3 py-1 text-base"
                >
                  {formatMoney(b.available, b.currency)}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  ← {formatMoney(b.redeemed_total, b.currency)} resgatado −{' '}
                  {formatMoney(b.spent_total, b.currency)} usado
                </span>
              </div>
            ))
          )}
          {insufficient && (
            <p className="text-sm text-amber-400">
              Saldo abaixo do preço — resgate um gift card antes.{' '}
              <Link to="/topup" className="underline underline-offset-4">
                Ver gift cards
              </Link>
            </p>
          )}
        </CardContent>
      </Card>

      {/* 2-4. Credenciais */}
      {revealLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : revealError || !creds ? (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">
            {revealError ?? 'Credenciais indisponíveis.'}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Email */}
          <div className="space-y-1">
            <Label className="text-xs uppercase text-muted-foreground">Email</Label>
            <div className="flex items-center gap-3">
              <div className="flex h-14 min-w-0 flex-1 items-center rounded-md border bg-background px-4">
                <span className="truncate font-mono text-lg">{creds.email}</span>
              </div>
              <CopyButton value={creds.email} label="Email" size="lg" plainToast />
            </div>
          </div>

          {/* Senha */}
          <div className="space-y-1">
            <Label className="text-xs uppercase text-muted-foreground">Senha</Label>
            <div className="flex items-center gap-3">
              <div className="flex h-14 min-w-0 flex-1 items-center gap-2 rounded-md border bg-background px-4">
                <span className="min-w-0 flex-1 truncate font-mono text-lg">
                  {creds.password
                    ? showPassword
                      ? creds.password
                      : '•'.repeat(Math.min(creds.password.length, 16))
                    : '(não definida)'}
                </span>
                {creds.password && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                )}
              </div>
              {creds.password && (
                <CopyButton value={creds.password} label="Senha" size="lg" plainToast />
              )}
            </div>
          </div>

          {/* Código 2FA */}
          <div className="space-y-1">
            <Label className="text-xs uppercase text-muted-foreground">Código 2FA</Label>
            {creds.totp_secret && totpCode ? (
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1 rounded-md border bg-background px-4 py-3">
                  <div
                    className={cn(
                      'font-mono text-4xl tracking-[0.2em]',
                      totpSeconds <= 5 && 'text-amber-400',
                    )}
                  >
                    {totpCode.slice(0, 3)} {totpCode.slice(3)}
                  </div>
                  <div className="mt-3 h-1 w-full overflow-hidden rounded bg-muted">
                    <div
                      className={cn(
                        'h-full rounded transition-[width] duration-1000 ease-linear',
                        totpSeconds <= 5 ? 'bg-amber-400' : 'bg-primary',
                      )}
                      style={{ width: `${(totpSeconds / TOTP_PERIOD) * 100}%` }}
                    />
                  </div>
                  <p className="mt-1.5 text-xs text-muted-foreground">expira em {totpSeconds}s</p>
                </div>
                <CopyButton value={totpCode} label="Código 2FA" size="lg" plainToast />
              </div>
            ) : (
              <p className="rounded-md border bg-background px-4 py-3 text-sm text-muted-foreground">
                {creds.totp_secret
                  ? 'Gerando código…'
                  : 'Sem TOTP configurado nesta conta.'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* 5. Registrar jogo comprado */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base uppercase tracking-wide">
            Registrar jogo comprado
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {account.status === 'game_purchased' && (
            <p className="text-sm text-amber-400">
              Esta conta já tem jogo comprado — pronta pra importar.
            </p>
          )}

          <div className="space-y-2">
            <Label>Jogo</Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar jogo no catálogo…"
                className="pl-9"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                spellCheck={false}
                autoComplete="off"
              />
            </div>
            <Select value={selectedRef || undefined} onValueChange={(v) => {
              setSelectedRef(v);
              const g = catalog.find((c) => catalogValue(c) === v);
              if (g && (g.access_count === 3 || g.access_count === 5)) setAccessCount(g.access_count);
            }}>
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    catalogLoading
                      ? 'Carregando catálogo…'
                      : catalog.length === 0
                        ? 'Nenhum jogo encontrado'
                        : 'Selecionar jogo'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {catalog.map((g) => (
                  <SelectItem key={catalogValue(g)} value={catalogValue(g)}>
                    {g.game_title}
                    {g.our_price_brl != null ? ` — ${formatMoney(g.our_price_brl, 'BRL')}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!selectedGame && searchQ.trim() && !catalogLoading && (
              <p className="text-xs text-muted-foreground">
                Sem seleção no catálogo — o texto da busca será usado como título.
              </p>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="purchase-price">Preço</Label>
              <Input
                id="purchase-price"
                inputMode="decimal"
                placeholder="0,00"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label>Moeda</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {currencyOptions.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Rende</Label>
            <div className="flex gap-2" role="radiogroup" aria-label="Quantidade de acessos">
              {([3, 5] as const).map((n) => (
                <Button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={accessCount === n}
                  variant={accessCount === n ? 'default' : 'outline'}
                  onClick={() => setAccessCount(n)}
                >
                  {n} acessos{n === 5 ? ' (cross-gen)' : ' (só PS5)'}
                </Button>
              ))}
            </div>
          </div>

          <Button
            size="lg"
            className="w-full"
            variant={insufficient ? 'outline' : 'default'}
            disabled={submitting || !effectiveTitle || !priceValid}
            onClick={onConfirm}
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Confirmar compra
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
